import { Button, Checkbox, Group, Radio, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { zodResolver } from "mantine-form-zod-resolver";
import { IMaskInput } from "react-imask";
import { z } from "zod";
import { IEvent } from "../../../models/events";
import { useEventStore } from "../../../store/events.store";
import { useDrawerStore } from "../../../store/dashboard.store";
import { useState } from "react";
import { IPatient } from "../../../models/patient";
import dayjs from "dayjs";

const EditPatient = ({ event, patient } : {event: IEvent, patient: IPatient}) => {
    const updatePatient = useEventStore((state) => state.updatePatient);
    const popPage = useDrawerStore((state) => state.popPage);
    const [loading, setLoading] = useState(false);
    const generalScheme = {
        firstName: z.string().min(2, { message: 'First name is too short' }),
        lastName: z.string().min(2, { message: 'Last name is too short' }),
        phone: z.string().min(10, { message: 'Phone number is too short' }),
        email: z.string().nullable(),
        dob: z.date(),
        officeStaffOnly: z.boolean().default(false),
        isNewPatient: z.enum(['yes', 'no']).default('no'),
    };

    const scheme = z.object(generalScheme);

    const form = useForm({
       mode: 'uncontrolled',
       validate: zodResolver(scheme),
       initialValues: {
            firstName: patient.firstName,
            lastName: patient.lastName,
            phone: patient.phone,
            email: patient.email,
            dob: dayjs( patient.dob).toDate(),
            homeAddress: patient.address,
            city:  patient.city,
            zipCode:  patient.zipCode,
            officeStaffOnly: patient.officeStaffOnly,
            isNewPatient: patient.isNewPatient,
       }
    });
    return (
        <form onSubmit={form.onSubmit(async (values) => {
            setLoading(true);
            await updatePatient(event.uid!, patient.uid!, {
                firstName: values.firstName,
                lastName: values.lastName,
                phone: values.phone,
                email: values.email,
                dob: dayjs(values.dob).toDate().toISOString().split('T')[0],
                address: values.homeAddress,
                city: values.city,
                zipCode: values.zipCode,
                officeStaffOnly: values.officeStaffOnly,
                isNewPatient: values.isNewPatient,
                latestEventId: event.uid,
                events: [event.uid!],
            });
            setLoading(false);
            popPage();
        })}>
            <Group grow>
                <TextInput {...form.getInputProps('firstName')} label="First Name" placeholder="Enter your first name" mb="md" />
                <TextInput {...form.getInputProps('lastName')} label="Last Name" placeholder="Enter your last name" mb="md" />
            </Group>
            
            <TextInput {...form.getInputProps('phone')}
                component={IMaskInput}
                // @ts-expect-error - IMaskInput props are not typed
                mask="(000) 000-0000"
                type="tel"
                label="Phone" placeholder="Enter your phone number" mb="md" />
            <TextInput {...form.getInputProps('email')} label="Email" placeholder="Enter your email" mb="md" />
            <h4>Date of Birth</h4>
            <Group grow>
                <DateInput
                    label="Date of Birth"
                    placeholder="Select date of birth"
                    required
                    {...form.getInputProps('dob')}
                />
            </Group>
                <h4>Home Address</h4>
                <TextInput {...form.getInputProps('homeAddress')} label="Address" placeholder="Enter your address" mb="md" />
                <Group grow >
                    <TextInput {...form.getInputProps('city')} label="City" placeholder="Enter your city" mb="md" />
                    <TextInput {...form.getInputProps('zipCode')} label="Zip Code" placeholder="Enter your zip code" mb="md" />
                </Group>
            <>
                <h4>Additional Information</h4>
                <Checkbox 
                    {...form.getInputProps('officeStaffOnly', { type: 'checkbox'})} 
                    label="Please check this box if you need to ask a question to office staff and no doctor visit needed." mb="md" 
                    key={form.key('officeStaffOnly')} />
                <Radio.Group  
                    label="Are you a new patient?"
                    description="Please select if you are a new patient or not"
                    withAsterisk
                    name="isNewPatient"
                    {...form.getInputProps('isNewPatient')}>
                    <Radio value="no" label="No, I am not a new patient" mb="md" />
                    <Radio value="yes" label="Yes, I am a new patient" mb="md" />
                </Radio.Group>
            </>
            <br />
            <Button 
                loading={loading} 
                disabled={loading} 
                variant="outline" 
                color="blue"
                type="submit">
                Update Patient
            </Button>
            <br />
            <br />
            <br />
        </form>
    );
};

export default EditPatient;